import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useCognitoSignOut } from '../auth';
import NavSearch from '../components/NavSearch';
import { MenuGlyph, MoonGlyph, SunGlyph } from '../components/Glyphs';

const TABS: [string, string][] = [
  ['/', 'Dashboard'],
  ['/search', 'Search'],
  ['/reports', 'Reports'],
  ['/maps', 'Maps'],
  ['/interactions', 'Interactions'],
  ['/tags', 'Tags'],
  ['/changes', 'Changes'],
  ['/incidents', 'Command'],
];

export default function Layout() {
  const auth = useAuth();
  const signOut = useCognitoSignOut();
  const email = auth.user?.profile.email ?? 'signed in';
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setMenuOpen(false), [pathname]);

  return (
    <div className="flex h-full flex-col">
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 print:hidden">
        {/* Wide screens: tabs and account controls in one row. */}
        <div className="mx-auto hidden max-w-7xl items-center justify-between px-4 py-2 lg:flex">
          <div className="flex items-center gap-6">
            <span className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-100">Steward</span>
            <nav className="flex gap-4 text-sm">
              {TABS.map(([to, label]) => <Tab key={to} to={to}>{label}</Tab>)}
            </nav>
          </div>
          <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            <NavSearch />
            <span>{email}</span>
            <ThemeToggle />
            <SignOutButton onClick={() => void signOut()} />
          </div>
        </div>
        {/* Phones and tablets: search stays in reach; the rest folds into a menu. */}
        <div className="lg:hidden">
          <div className="flex items-center gap-3 px-4 py-2">
            <span className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-100">Steward</span>
            <div className="min-w-0 flex-1">
              <NavSearch />
            </div>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-expanded={menuOpen}
              aria-controls="nav-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="rounded border border-slate-300 dark:border-slate-600 p-1.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <MenuGlyph open={menuOpen} />
            </button>
          </div>
          {menuOpen && (
            <div id="nav-menu" className="border-t border-slate-200 dark:border-slate-700 px-4 pb-3">
              <nav className="grid grid-cols-2 gap-x-4 py-2 text-sm">
                {TABS.map(([to, label]) => <MenuTab key={to} to={to}>{label}</MenuTab>)}
              </nav>
              <div className="flex items-center gap-3 border-t border-slate-200 dark:border-slate-700 pt-3 text-xs text-slate-500 dark:text-slate-400">
                <span className="min-w-0 flex-1 truncate">{email}</span>
                <ThemeToggle />
                <SignOutButton onClick={() => void signOut()} />
              </div>
            </div>
          )}
        </div>
      </header>
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-4 py-4">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function ThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.classList.contains('dark'));
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle('dark', next);
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };
  return (
    <button
      onClick={toggle}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-300"
    >
      {dark ? <SunGlyph /> : <MoonGlyph />}
    </button>
  );
}

function Tab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `border-b-2 py-1 ${isActive ? 'border-slate-900 dark:border-slate-100 text-slate-900 dark:text-slate-100' : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`
      }
    >
      {children}
    </NavLink>
  );
}

function MenuTab({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `rounded px-2 py-2 ${isActive ? 'bg-slate-100 dark:bg-slate-800 font-medium text-slate-900 dark:text-slate-100' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
      }
    >
      {children}
    </NavLink>
  );
}

function SignOutButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="rounded border border-slate-300 dark:border-slate-600 px-2 py-1 hover:bg-slate-100 dark:hover:bg-slate-700"
    >
      Sign out
    </button>
  );
}
